import { useInfiniteGantnerDevices } from 'api/GantnerDevices'
import {
  useGetNotificationPreferences,
  useUpdateNotificationPreferences,
} from 'api/GantnerUsageNotifications'
import { SETTINGS_MANAGE } from 'constants/permissions'
import { Form, Formik } from 'formik'
import { useCurrentUserCanForCurrentSite } from 'hooks/UseCurrentUserCan'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from 'ui/components/Badge'
import { Button } from 'ui/components/Button'
import { RadioCardOption, RadioCards } from 'ui/components/Form/RadioCards/RadioCards'
import { Select, SelectOption } from 'ui/components/Form/Select'
import {
  ApiErrorNotification,
  Notification,
  useNotificationContext,
} from 'ui/components/Notification'
import { SettingInputBlock } from 'ui/components/Settings/SettingInputBlock'
import { Spinner } from 'ui/components/Spinner'

const DevicePicker = ({ onChange, value }) => {
  const {
    hasNextPage,
    fetchNextPage,
    data: devices,
    isFetchingNextPage,
  } = useInfiniteGantnerDevices()

  const options = (devices?.pages ?? [])
    .map((page) =>
      (page?.data ?? []).map((product) => ({
        value: product.id,
        name: product.name,
      }))
    )
    .flat()

  return (
    <div className="space-y-2">
      <Select
        options={options}
        selected={value}
        multiple
        isLoadingMore={isFetchingNextPage}
        loadMore={() => fetchNextPage()}
        hasMore={hasNextPage}
        onChange={onChange}
      />

      <div>
        {value.map((val) => (
          <Badge
            key={val.value}
            label={val.name}
            variant="info"
            removeIcon
            onRemove={() => onChange(value.filter((v) => v.value !== val.value))}
          />
        ))}
      </div>
    </div>
  )
}

export const NotificationSettings = () => {
  const { t } = useTranslation()
  const can = useCurrentUserCanForCurrentSite()
  const userCanManageSettings = can(SETTINGS_MANAGE)
  const notifications = useNotificationContext()
  const { isLoading, data } = useGetNotificationPreferences()

  const { mutateAsync } = useUpdateNotificationPreferences()

  const types: SelectOption<string | null>[] = [
    {
      name: t('settings.integrations.gantner.notification_settings.types.name.off'),
      value: null,
      description: t('settings.integrations.gantner.notification_settings.types.description'),
    },
    {
      name: t('settings.integrations.gantner.notification_settings.types.name.access_granted_only'),
      value: 'access_granted_only',
      description: t(
        'settings.integrations.gantner.notification_settings.types.description.access_granted_only'
      ),
    },
    {
      name: t('settings.integrations.gantner.notification_settings.types.name.access_denied_only'),
      value: 'access_denied_only',
      description: t(
        'settings.integrations.gantner.notification_settings.types.description.access_denied_only'
      ),
    },
    {
      name: t('settings.integrations.gantner.notification_settings.types.name.all'),
      value: 'all_notifications',
      description: t('settings.integrations.gantner.notification_settings.types.description.all'),
    },
  ]

  const scopes: RadioCardOption<string>[] = [
    {
      title: t('settings.integrations.gantner.notification_settings.scopes.title.all_devices'),
      id: 'all',
      description: t(
        'settings.integrations.gantner.notification_settings.scopes.description.all_devices'
      ),
    },
    {
      title: t('settings.integrations.gantner.notification_settings.scopes.title.specific_devices'),
      id: 'specific',
      description: t(
        'settings.integrations.gantner.notification_settings.scopes.description.specific_devices'
      ),
    },
  ]

  const [scope, setScope] = useState(data?.data?.devices?.length ?? 0 > 0 ? scopes[1] : scopes[0])

  const initialValues = {
    devices: data?.data?.devices ?? [],
    type: data?.data?.type,
  }

  const handleSubmit = (vals) => {
    return mutateAsync(
      {
        ...vals,
        devices: vals.devices.map((d) => d.value).filter(Boolean),
      },
      {
        onSuccess: () => {
          notifications.notify(
            <Notification
              title={t('settings.integrations.gantner.notification_settings.notification.title')}
              description={t(
                'settings.integrations.gantner.notification_settings.notification.description'
              )}
              variant="success"
              autoDismiss
            />
          )
        },
        onError: (e: any) => {
          notifications.notify(<ApiErrorNotification error={e} />)
        },
      }
    )
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ values, setFieldValue, isSubmitting }) => (
        <Form>
          <SettingInputBlock
            label={t('settings.integrations.gantner.notification_settings.title')}
            isNew
            description={[
              t('settings.integrations.gantner.notification_settings.description_line_first'),
              t('settings.integrations.gantner.notification_settings.description_line_second'),
            ]}
          >
            <div className="space-y-4">
              <div>
                <Select
                  options={types}
                  selected={types.find((t) => t.value === values.type)}
                  onChange={(val) => setFieldValue('type', val?.value)}
                  label={t(
                    'settings.integrations.gantner.notification_settings.notification_type_select.label'
                  )}
                  disabled={!userCanManageSettings}
                />
              </div>

              {values.type !== null && (
                <RadioCards
                  value={scope}
                  options={scopes}
                  onChange={(newScope) => {
                    setScope(newScope)

                    if (newScope.id === 'all') {
                      setFieldValue('devices', [])
                    }
                  }}
                />
              )}

              {scope.id === 'specific' && (
                <div className="space-y-1">
                  <DevicePicker
                    onChange={(val) => setFieldValue('devices', val)}
                    value={values.devices}
                  />

                  <p className="text-sm text-gray-500">
                    {t(
                      'settings.integrations.gantner.notification_settings.device_picker.description'
                    )}
                  </p>
                </div>
              )}

              <div className="text-right">
                <Button
                  type="submit"
                  label={t('button.save_changes')}
                  loading={isSubmitting}
                  disabled={!userCanManageSettings}
                />
              </div>
            </div>
          </SettingInputBlock>
        </Form>
      )}
    </Formik>
  )
}
